/**
 * @quotech/auth - Unified SSO Page Component
 * 
 * PATH: packages/auth/src/UnifiedSSOPage.tsx
 * PURPOSE: Standardized SSO handling page for all Quotech hubs
 * 
 * USAGE:
 * ```tsx
 * // In your hub's router:
 * import { UnifiedSSOPage } from '@quotech/auth'
 * 
 * <Route path="/sso" element={
 *   <UnifiedSSOPage 
 *     hubName="CRM" 
 *     onSuccess={() => navigate('/')}
 *   />
 * } />
 * ```
 * 
 * FEATURES:
 * - Consistent token validation
 * - Organisation context handling
 * - Job context extraction
 * - Loading states with branding
 * - Error handling with retry
 * - Automatic redirect after auth
 */

import { useEffect, useState, useCallback } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from './authStore'
import { decodeContext, type JobContext } from './ssoUtils'
import { MASTER_LOGIN_URL } from './cookieAuth'

// =============================================================================
// TYPES
// =============================================================================

export interface UnifiedSSOPageProps {
  /** Hub display name (e.g., "CRM", "QA", "Workforce") - derived from URL if not provided */
  hubName?: string
  /** Hub accent color for theming */
  accentColor?: string
  /** Hub accent gradient */
  accentGradient?: string
  /** Callback when SSO succeeds */
  onSuccess?: (params: { returnTo: string; jobContext?: JobContext }) => void
  /** Callback when SSO fails */
  onError?: (error: string) => void
  /** Default path to redirect to after success (alias: defaultRedirectPath) */
  defaultRedirect?: string
  /** Default path to redirect to after success (alias for defaultRedirect) */
  defaultRedirectPath?: string
  /** Custom loading component */
  loadingComponent?: React.ReactNode
  /** Custom error component */
  errorComponent?: React.ComponentType<{ error: string; onRetry: () => void }>
  /** Validate token with backend API */
  validateToken?: (token: string) => Promise<{ user: Record<string, unknown>; organisation?: Record<string, unknown> }>
  /** Whether to clear existing auth before processing SSO */
  clearExistingAuth?: boolean
  /**
   * BUG-2026-05-25-LOGIN-BOUNCE-SSO-REDIRECT-UNGATED: Optional gate for
   * cross-SPA `window.location.replace(finalRedirectPath)` calls. Return
   * false to suppress the redirect (the page will render the error
   * component with a "loop detected" message so the operator can act).
   * Wired by `src/App.jsx` to `recordLoginBounceAttempt(targetPath)
   * .allowed` so the per-URL counter shared with the master signin's
   * `performSsoRedirect` (gated separately in
   * `src/Pages/Auth/signin/sso-redirect.js`) and the SAM SPA's
   * no-cookie auto-redirect (gated separately in
   * `apps/sam/src/app/App.tsx`) all funnel into the same breaker. The
   * three-site funnel reduces the worst-case loop from ~10 visible
   * navigations to ~5.
   *
   * Lifted to a prop (rather than imported here) because `@quotech/auth`
   * deliberately does NOT depend on `@quotech/ui` — the breaker lives in
   * `packages/ui/src/auth/loginBounceBreaker.ts` and flipping the
   * import direction would close a circular dep. Same pattern the
   * SessionManager uses to receive an `onSessionExpired` callback from
   * the master SPA without importing master code.
   *
   * Contract: called BEFORE every `window.location.replace` to a
   * cross-SPA path. If returns false, the redirect is suppressed and
   * `status` is set to 'error' so the user sees the error component.
   * If omitted, the breaker is effectively bypassed and cross-SPA
   * replaces happen unconditionally (preserves pre-MR behaviour).
   */
  shouldRedirect?: (targetPath: string) => boolean
}

type SSOStatus = 'loading' | 'validating' | 'success' | 'error'

// =============================================================================
// CROSS-SPA DETECTION
// =============================================================================

/**
 * Subpath-hosted hubs that are served by Nginx as independent SPAs under a
 * path prefix on dashboard.quotech.ai. Post-SSO redirects that target these
 * paths must use a full page load (window.location) rather than React Router
 * navigate(), because the main dashboard SPA doesn't own those routes.
 *
 * Keep this in sync with deploy/nginx configuration. When adding a new
 * subpath-hosted hub, append its base path (with trailing slash) here.
 */
const SUBPATH_SPA_BASES: readonly string[] = [
  // Multi-tenant company dashboards served by Nginx as independent SPAs
  // under /company/<slug>/ (see unified-layout/index.tsx handleHubClick
  // which also does a window.location.href for the same reason).
  '/company/',
  // Legacy path alias for Sydney Architectural Metal — kept for any old
  // links/bookmarks that still target the pre-migration URL. Nginx 301s
  // this to /company/sam/, but including it keeps the no-token early
  // return path correct too.
  '/sydney-architectural-metal/',
]

/**
 * Returns true when `returnTo` points to a different SPA than the currently
 * running one and therefore requires a full page navigation.
 */
function isCrossSpaReturnTo(returnTo: string): boolean {
  if (!returnTo || !returnTo.startsWith('/')) return false
  const base = (import.meta.env.BASE_URL || '/').replace(/\/$/, '')
  if (base) {
    // We're a subpath SPA (e.g., Steel /sydney-architectural-metal/).
    // Anything outside our own base path is cross-SPA.
    return !returnTo.startsWith(base + '/') && returnTo !== base
  }
  // We're the main dashboard SPA (BASE_URL === "/"). Paths that belong to a
  // known subpath-hosted hub need a hard navigation so Nginx picks them up.
  return SUBPATH_SPA_BASES.some(
    (p) => returnTo === p || returnTo === p.slice(0, -1) || returnTo.startsWith(p),
  )
}

// =============================================================================
// PARAM EXTRACTION
// =============================================================================

interface SSOParams {
  token: string | null
  refreshToken: string | null
  orgId: string | null
  orgName: string | null
  returnTo: string | null
  context: Record<string, unknown> | null
  jobContext: JobContext | null
}

function extractSSOParams(searchParams: URLSearchParams): SSOParams {
  const token = searchParams.get('token')
  const refreshToken = searchParams.get('refresh_token') || searchParams.get('refreshToken')
  const orgId = searchParams.get('org_id') || searchParams.get('orgId')
  const orgName = searchParams.get('org_name') || searchParams.get('orgName')
  const returnTo = searchParams.get('returnTo') || searchParams.get('return_to') || searchParams.get('redirect') || searchParams.get('to')
  
  // Decode context if present
  const contextStr = searchParams.get('context')
  let context: Record<string, unknown> | null = null
  let jobContext: JobContext | null = null
  
  if (contextStr) {
    context = decodeContext(contextStr)
    if (context?.job) {
      jobContext = context.job as JobContext
    }
  }
  
  // Also check direct job context param
  const jobContextStr = searchParams.get('jobContext')
  if (jobContextStr && !jobContext) {
    jobContext = decodeContext<JobContext>(jobContextStr)
  }
  
  return {
    token,
    refreshToken,
    orgId,
    orgName,
    returnTo,
    context,
    jobContext,
  }
}

// =============================================================================
// DEFAULT COMPONENTS
// =============================================================================

function DefaultLoading({ hubName, accentGradient }: { hubName: string; accentGradient: string }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-900">
      {/* Quotech Logo */}
      <div className="mb-8 flex items-center gap-3">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${accentGradient} flex items-center justify-center shadow-lg`}>
          <span className="text-white font-bold text-xl">Q</span>
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-800 dark:text-slate-200">
            {hubName}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Powered by Quotech
          </p>
        </div>
      </div>
      
      {/* Loading spinner */}
      <div className="relative">
        <div className="w-12 h-12 border-4 border-slate-200 dark:border-slate-700 rounded-full"></div>
        <div className={`absolute top-0 left-0 w-12 h-12 border-4 border-transparent border-t-teal-500 rounded-full animate-spin`}></div>
      </div>
      
      <p className="mt-6 text-slate-600 dark:text-slate-400">
        Authenticating...
      </p>
      <p className="mt-2 text-sm text-slate-400 dark:text-slate-500">
        Please wait while we verify your credentials
      </p>
    </div>
  )
}

function DefaultError({ 
  error, 
  hubName, 
  onRetry 
}: { 
  error: string
  hubName: string
  onRetry: () => void
}) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-900 p-4">
      {/* Error icon */}
      <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-6">
        <svg 
          className="w-8 h-8 text-red-600 dark:text-red-400" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
          />
        </svg>
      </div>
      
      <h1 className="text-xl font-bold text-slate-800 dark:text-slate-200 mb-2">
        Authentication Failed
      </h1>
      
      <p className="text-slate-600 dark:text-slate-400 text-center max-w-md mb-6">
        {error}
      </p>
      
      <div className="flex gap-3">
        <button
          onClick={onRetry}
          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
        >
          Try Again
        </button>
        <a
          href={MASTER_LOGIN_URL}
          className="px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors font-medium"
        >
          Return to Dashboard
        </a>
      </div>
      
      <p className="mt-8 text-sm text-slate-400 dark:text-slate-500">
        Please try accessing {hubName} through the Quotech Dashboard
      </p>
    </div>
  )
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * Derive hub name from the current hostname
 */
function getHubNameFromUrl(): string {
  if (typeof window === 'undefined') return 'Quotech'
  
  const hostname = window.location.hostname
  const subdomain = hostname.split('.')[0]
  
  // Map subdomains to display names
  const hubNames: Record<string, string> = {
    dashboard: 'Jobs',
    qa: 'QA Hub',
    crm: 'CRM',
    qdrive: 'QDrive',
    workforce: 'Workforce',
    cfo: 'CFO',
    billing: 'Billing',
    email: 'Email',
    quote: 'Quote Builder',
    ai: 'AI Hub',
    hr: 'HR Hub',
    inventory: 'Inventory',
    assets: 'Assets',
  }
  
  return hubNames[subdomain] || 'Quotech'
}

/**
 * Derive accent gradient from hub
 */
function getAccentGradientFromUrl(): string {
  if (typeof window === 'undefined') return 'from-teal-500 to-teal-600'
  
  const hostname = window.location.hostname
  const subdomain = hostname.split('.')[0]
  
  // Map subdomains to accent gradients
  const gradients: Record<string, string> = {
    dashboard: 'from-teal-500 to-teal-600',
    qa: 'from-teal-500 to-emerald-600',
    crm: 'from-indigo-500 to-purple-600',
    qdrive: 'from-blue-500 to-cyan-600',
    workforce: 'from-teal-500 to-cyan-600',
    cfo: 'from-cyan-500 to-blue-600',
    billing: 'from-emerald-500 to-green-600',
    email: 'from-rose-500 to-pink-600',
    quote: 'from-violet-500 to-purple-600',
    ai: 'from-cyan-500 to-sky-600',
    hr: 'from-indigo-500 to-blue-600',
    inventory: 'from-stone-500 to-stone-600',
    assets: 'from-slate-500 to-slate-600',
  }
  
  return gradients[subdomain] || 'from-teal-500 to-teal-600'
}

export function UnifiedSSOPage({
  hubName: hubNameProp,
  accentColor = 'teal',
  accentGradient: accentGradientProp,
  onSuccess,
  onError,
  defaultRedirect,
  defaultRedirectPath,
  loadingComponent,
  errorComponent: ErrorComponent,
  validateToken,
  clearExistingAuth = false,
  shouldRedirect,
}: UnifiedSSOPageProps) {
  // Use provided name/gradient or derive from URL
  const hubName = hubNameProp || getHubNameFromUrl()
  const accentGradient = accentGradientProp || getAccentGradientFromUrl()
  // Support both prop names for redirect path
  const redirectPath = defaultRedirect || defaultRedirectPath || '/'
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  
  const { 
    setFromSSO, 
    isAuthenticated, 
    clearAuth,
    updateTokens,
    updateUser,
  } = useAuthStore()
  // Extended property that may be added by hub-specific store extensions
  const updateOrganisation = (useAuthStore.getState() as unknown as Record<string, unknown>).updateOrganisation as
    ((id: string, name?: string) => void) | undefined
  
  const [status, setStatus] = useState<SSOStatus>('loading')
  const [error, setError] = useState<string>('')
  const [params, setParams] = useState<SSOParams | null>(null)
  
  // Extract params on mount
  useEffect(() => {
    const extracted = extractSSOParams(searchParams)
    setParams(extracted)
  }, [searchParams])
  
  // Process SSO
  const processSSO = useCallback(async () => {
    if (!params) return
    
    const { token, refreshToken, orgId, orgName, returnTo, jobContext } = params
    
    // No token in URL
    if (!token) {
      // If already authenticated, just redirect
      if (isAuthenticated && returnTo) {
        if (isCrossSpaReturnTo(returnTo)) {
          // BUG-2026-05-25-LOGIN-BOUNCE-SSO-REDIRECT-UNGATED: gate the
          // cross-SPA replace through the caller-supplied breaker so a
          // loop between this /sso page and the destination SPA (when
          // the destination SPA can't see the cookie and bounces back)
          // trips after the same per-URL threshold the other two bounce
          // sites (master signin's performSsoRedirect + SAM no-cookie
          // redirect) already honour.
          if (shouldRedirect && !shouldRedirect(returnTo)) {
            setStatus('error')
            setError(
              'We tried to open your dashboard but the page kept bouncing back here. ' +
              'Click below to try again — the next attempt will go through.',
            )
            onError?.('login bounce breaker tripped on cross-SPA SSO redirect')
            return
          }
          window.location.replace(returnTo)
        } else {
          navigate(returnTo, { replace: true })
        }
        return
      }
      
      // Not authenticated and no token - error
      if (!isAuthenticated) {
        setStatus('error')
        setError(`No authentication token provided. Please access ${hubName} from the Quotech Dashboard.`)
        onError?.(`No authentication token provided`)
        return
      }
      
      // Authenticated but no returnTo - go to default
      navigate(redirectPath, { replace: true })
      return
    }
    
    // Clear existing auth if requested
    if (clearExistingAuth) {
      clearAuth()
    }
    
    setStatus('validating')
    
    try {
      // Validate token if validator provided
      if (validateToken) {
        const result = await validateToken(token)
        
        // Update auth store with validated data
        updateTokens(token, refreshToken || undefined)
        
        if (result.user) {
          const u = result.user
          updateUser({
            id: String(u.id || u.user_id || ''),
            email: String(u.email || ''),
            name: String(u.full_name || u.name || u.email || ''),
          })
        }
        
        if ((result.organisation || orgId) && updateOrganisation) {
          const org = result.organisation
          updateOrganisation(
            String(org?.id ?? '') || orgId || '',
            String(org?.name ?? '') || orgName || undefined
          )
        }
      } else {
        // No validator - just store the tokens
        // Use setFromSSO for complete SSO handling (positional args: token, orgId, orgName)
        await setFromSSO(token, orgId || undefined, orgName || undefined)
      }
      
      setStatus('success')
      
      // Clean URL
      const newParams = new URLSearchParams(searchParams)
      // Remove all SSO params
      const paramsToRemove = [
        'token', 'refresh_token', 'refreshToken',
        'org_id', 'orgId', 'org_name', 'orgName',
        'returnTo', 'return_to', 'redirect', 'to',
        'context', 'jobContext',
      ]
      paramsToRemove.forEach(p => newParams.delete(p))
      setSearchParams(newParams, { replace: true })
      
      // Call success callback or navigate
      const finalRedirectPath = returnTo || redirectPath
      
      if (onSuccess) {
        onSuccess({ returnTo: finalRedirectPath, jobContext: jobContext || undefined })
      } else {
        // Small delay to show success state
        setTimeout(() => {
          if (isCrossSpaReturnTo(finalRedirectPath)) {
            // BUG-2026-05-25-LOGIN-BOUNCE-SSO-REDIRECT-UNGATED: gate the
            // post-SSO cross-SPA replace through the caller-supplied
            // breaker. This is the SECOND bounce site in the master
            // signin <-> /sso <-> tenant loop. Keying on
            // `finalRedirectPath` (e.g. "/company/sam/jobs") — stable
            // across iterations — accumulates the per-URL count in
            // parallel with `performSsoRedirect`'s gate at the
            // upstream step, so the loop trips on iteration 2
            // instead of iteration 3.
            //
            // Target belongs to a different SPA (e.g. Steel under
            // /sydney-architectural-metal/) — Nginx must route it, so do a
            // full page load instead of client-side navigation.
            if (shouldRedirect && !shouldRedirect(finalRedirectPath)) {
              setStatus('error')
              setError(
                'You\'re signed in, but we kept bouncing between this page and your dashboard. ' +
                'Click below to try again — the next attempt will go through.',
              )
              onError?.('login bounce breaker tripped on post-SSO cross-SPA redirect')
              return
            }
            window.location.replace(finalRedirectPath)
          } else {
            navigate(finalRedirectPath, { replace: true })
          }
        }, 300)
      }
      
    } catch (err) {
      console.error('[@quotech/auth] SSO validation failed:', err)
      setStatus('error')
      const message = err instanceof Error ? err.message : 'Authentication failed. Please try again.'
      setError(message)
      onError?.(message)
    }
  }, [
    params, 
    isAuthenticated, 
    navigate, 
    clearExistingAuth,
    clearAuth,
    setFromSSO,
    updateTokens,
    updateUser,
    updateOrganisation,
    validateToken,
    searchParams,
    setSearchParams,
    redirectPath,
    hubName,
    onSuccess,
    onError,
    shouldRedirect,
  ])
  
  // Process SSO when params are available
  useEffect(() => {
    if (params) {
      processSSO()
    }
  }, [params, processSSO])
  
  // Retry handler
  const handleRetry = useCallback(() => {
    setStatus('loading')
    setError('')
    processSSO()
  }, [processSSO])
  
  // Render based on status
  if (status === 'error') {
    if (ErrorComponent) {
      return <ErrorComponent error={error} onRetry={handleRetry} />
    }
    return <DefaultError error={error} hubName={hubName} onRetry={handleRetry} />
  }
  
  // Loading/validating states
  if (loadingComponent) {
    return <>{loadingComponent}</>
  }
  
  return <DefaultLoading hubName={hubName} accentGradient={accentGradient} />
}

export default UnifiedSSOPage

